import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Users, UserPlus, Mail, Trash2, ToggleLeft, ToggleRight, 
  Shield, RefreshCw, Search, AlertCircle, CheckCircle2, Clock, Send,
  Megaphone, AlertTriangle, Crown, BookOpen, CalendarPlus, X, Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Admin = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviting, setInviting] = useState(false);
  
  // Broadcast email state
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastType, setBroadcastType] = useState("transition"); // "transition" or "inactive"
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [inactiveUsers, setInactiveUsers] = useState([]);

  // Custom broadcast state
  const [showCustomBroadcast, setShowCustomBroadcast] = useState(false);
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [customRecipientMode, setCustomRecipientMode] = useState("all"); // "all" | "active" | "inactive" | "selected"
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendingCustom, setSendingCustom] = useState(false);
  const [customSentResult, setCustomSentResult] = useState(null);
  
  // Beta Access Modal state
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [betaUser, setBetaUser] = useState(null);
  const [betaDays, setBetaDays] = useState(90);
  const [updatingBeta, setUpdatingBeta] = useState(false);
  
  // Book Verification state
  const [bookClaims, setBookClaims] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [activeTab, setActiveTab] = useState("users"); // "users" or "book"

  // Allocate Access state
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [allocateSearch, setAllocateSearch] = useState("");
  const [allocateSelected, setAllocateSelected] = useState([]); // array of emails
  const [allocateMonths, setAllocateMonths] = useState(1);
  const [allocateCustomMonths, setAllocateCustomMonths] = useState("");
  const [allocateSubject, setAllocateSubject] = useState("");
  const [allocateMessage, setAllocateMessage] = useState("");
  const [allocateTab, setAllocateTab] = useState("users"); // "users" | "message"
  const [allocating, setAllocating] = useState(false);

  const checkAdmin = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/check`, { withCredentials: true });
      setIsAdmin(response.data.is_admin);
      if (!response.data.is_admin) {
        toast.error("Access denied", { description: "Admin privileges required" });
        navigate("/progress");
      }
    } catch (error) {
      navigate("/progress");
    }
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAdmin();
  }, [checkAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, fetchUsers]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail || !inviteName) return;

    setInviting(true);
    try {
      const response = await axios.post(`${API}/admin/invite`, 
        { email: inviteEmail, name: inviteName },
        { withCredentials: true }
      );
      
      const emailStatus = response.data.email_status;
      if (emailStatus === "mocked") {
        toast.success("User created!", { 
          description: "Email sending is mocked (no API key). Default password: ClarityOS2026" 
        });
      } else {
        toast.success("Invitation sent!", { 
          description: `${inviteEmail} has been invited to ClarityOS` 
        });
      }
      
      setInviteEmail("");
      setInviteName("");
      setShowInviteForm(false);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to invite user";
      toast.error(message);
    } finally {
      setInviting(false);
    }
  };

  const handleToggleUser = async (email, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${encodeURIComponent(email)}/toggle`, {}, { withCredentials: true });
      toast.success(currentStatus ? "User deactivated" : "User activated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleRemoveUser = async (email) => {
    if (!window.confirm(`Remove ${email} from beta access?`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success("User removed");
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to remove user";
      toast.error(message);
    }
  };

  const handleResendInvitation = async (email, name) => {
    try {
      const response = await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/resend`, {}, { withCredentials: true });
      const emailStatus = response.data.email_status;
      if (emailStatus === "mocked") {
        toast.success("Invitation reminder ready!", { 
          description: `Email mocked. Default password: ClarityOS2026` 
        });
      } else {
        toast.success("Invitation resent!", { 
          description: `Reminder sent to ${email}` 
        });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to resend invitation";
      toast.error(message);
    }
  };

  const handleBroadcastEmail = async (type) => {
    setSendingBroadcast(true);
    try {
      const endpoint = type === "transition" 
        ? `${API}/admin/email/broadcast-transition`
        : `${API}/admin/email/send-inactive-warnings`;
      
      const response = await axios.post(endpoint, {}, { withCredentials: true });
      
      toast.success(response.data.message, {
        description: `${response.data.sent_count || response.data.emails_sent || 0} emails sent`
      });
      
      setShowBroadcastModal(false);
      
      // Refresh inactive users count
      if (type === "inactive") {
        fetchInactiveUsers();
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send emails";
      toast.error(message);
    } finally {
      setSendingBroadcast(false);
    }
  };

  // Custom broadcast handler
  const handleSendCustomBroadcast = async () => {
    if (!customSubject.trim() || !customBody.trim()) {
      toast.error("Subject and message body are required");
      return;
    }
    setSendingCustom(true);
    setCustomSentResult(null);
    try {
      const payload = {
        subject: customSubject.trim(),
        body: customBody.trim(),
        recipient_mode: customRecipientMode,
        selected_emails: customRecipientMode === "selected" ? selectedRecipients : []
      };
      const response = await axios.post(`${API}/admin/email/custom-broadcast`, payload, { withCredentials: true });
      setCustomSentResult({ success: true, count: response.data.sent_count, failed: response.data.failed_count });
      toast.success(`Sent to ${response.data.sent_count} recipients`);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send email";
      toast.error(message);
      setCustomSentResult({ success: false });
    } finally {
      setSendingCustom(false);
    }
  };

  const toggleRecipient = (email) => {
    setSelectedRecipients(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const getRecipientCount = () => {
    if (customRecipientMode === "all") return users.length;
    if (customRecipientMode === "active") return users.filter(u => u.is_active !== false).length;
    if (customRecipientMode === "inactive") return inactiveUsers.length;
    if (customRecipientMode === "selected") return selectedRecipients.length;
    return 0;
  };

  const fetchInactiveUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users/inactive`, { withCredentials: true });
      setInactiveUsers(response.data.users || []);
    } catch (error) {
      console.error("Failed to fetch inactive users");
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchInactiveUsers();
      fetchBookClaims();
    }
  }, [isAdmin, fetchInactiveUsers]);

  // Fetch book verification claims
  const fetchBookClaims = async () => {
    setLoadingClaims(true);
    try {
      const response = await axios.get(`${API}/admin/book-verifications`, { withCredentials: true });
      setBookClaims(response.data.claims || []);
    } catch (error) {
      console.error("Failed to fetch book claims");
    } finally {
      setLoadingClaims(false);
    }
  };

  // Handle book claim action
  const handleBookClaimAction = async (claimId, action) => {
    try {
      await axios.put(
        `${API}/admin/book-verifications/${claimId}`,
        { action },
        { withCredentials: true }
      );
      
      if (action === "approve") {
        toast.success("Book verification approved! 6 months premium granted.");
      } else {
        toast.success("Book verification rejected.");
      }
      
      fetchBookClaims();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to process claim";
      toast.error(message);
    }
  };

  // Handle beta access toggle
  const handleBetaAccessToggle = async (enable) => {
    if (!betaUser) return;
    
    setUpdatingBeta(true);
    try {
      await axios.put(
        `${API}/admin/users/${betaUser.email}/beta-access`,
        { beta_mode: enable, beta_days: betaDays },
        { withCredentials: true }
      );
      
      if (enable) {
        toast.success("Beta access granted!", {
          description: `${betaUser.name} now has founding member access for ${betaDays} days`
        });
      } else {
        toast.success("Beta access revoked", {
          description: `${betaUser.name}'s beta access has been removed`
        });
      }
      
      setShowBetaModal(false);
      setBetaUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update beta access";
      toast.error(message);
    } finally {
      setUpdatingBeta(false);
    }
  };

  const openBetaModal = (user) => {
    setBetaUser(user);
    setBetaDays(90);
    setShowBetaModal(true);
  };

  const openAllocateModal = () => {
    setAllocateSearch("");
    setAllocateSelected([]);
    setAllocateMonths(1);
    setAllocateCustomMonths("");
    setAllocateSubject("");
    setAllocateMessage("");
    setAllocateTab("users");
    setShowAllocateModal(true);
  };

  const toggleAllocateUser = (email) => {
    setAllocateSelected(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const selectAllFiltered = () => {
    const filtered = users.filter(u =>
      u.email.toLowerCase().includes(allocateSearch.toLowerCase()) ||
      u.name.toLowerCase().includes(allocateSearch.toLowerCase())
    );
    const emails = filtered.map(u => u.email);
    const allSelected = emails.every(e => allocateSelected.includes(e));
    if (allSelected) {
      setAllocateSelected(prev => prev.filter(e => !emails.includes(e)));
    } else {
      setAllocateSelected(prev => [...new Set([...prev, ...emails])]);
    }
  };

  const getEffectiveMonths = () => {
    if (allocateMonths === "custom") {
      const n = parseInt(allocateCustomMonths);
      return isNaN(n) || n < 1 ? 0 : n;
    }
    return allocateMonths;
  };

  const handleAllocateAccess = async () => {
    const months = getEffectiveMonths();
    if (allocateSelected.length === 0) { toast.error("Select at least one user"); return; }
    if (months < 1) { toast.error("Enter a valid number of months (min 1)"); return; }
    setAllocating(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/allocate-access`,
        {
          emails: allocateSelected,
          months,
          subject: allocateSubject.trim() || null,
          message: allocateMessage.trim() || null,
        },
        { withCredentials: true }
      );
      toast.success(
        `Access allocated!`,
        { description: `${response.data.updated_count || allocateSelected.length} user(s) extended by ${months} month${months !== 1 ? "s" : ""}` }
      );
      setShowAllocateModal(false);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to allocate access";
      toast.error(message);
    } finally {
      setAllocating(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric",
      year: "numeric"
    });
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-clarity-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3" data-testid="admin-title">
            <Shield className="w-8 h-8 text-brand-primary" />
            Admin Dashboard
          </h1>
          <p className="text-slate-400 mt-1">Manage beta users and invitations</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={openAllocateModal}
            className="border-[#4CAF7A]/40 text-[#4CAF7A] hover:text-white hover:bg-[#4CAF7A]/10"
            data-testid="allocate-access-btn"
          >
            <CalendarPlus className="w-4 h-4 mr-2" />
            Allocate Access
          </Button>
          <Button 
            variant="outline" 
            onClick={() => { setCustomSubject(""); setCustomBody(""); setCustomRecipientMode("all"); setSelectedRecipients([]); setCustomSentResult(null); setShowCustomBroadcast(true); }}
            className="border-[var(--lock)]/40 text-[var(--lock)] hover:text-white hover:bg-[var(--lock)]/10"
            data-testid="custom-broadcast-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            Custom Email
          </Button>
          <Button 
            variant="outline" 
            onClick={() => { setBroadcastType("transition"); setShowBroadcastModal(true); }}
            className="border-brand-tertiary/30 text-brand-tertiary hover:text-white hover:bg-brand-tertiary/10"
            data-testid="broadcast-email-btn"
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Broadcast Email
          </Button>
          <Button 
            variant="outline" 
            onClick={fetchUsers}
            className="border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
            data-testid="refresh-users-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => setShowInviteForm(true)}
            className="bg-brand-primary text-clarity-bg font-bold hover:bg-yellow-400"
            data-testid="invite-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-primary/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-brand-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="total-users-count">{users.length}</p>
              <p className="text-slate-400 text-sm">Total Users</p>
            </div>
          </div>
        </div>
        <div className="glass rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{users.filter(u => u.is_active !== false).length}</p>
              <p className="text-slate-400 text-sm">Active Users</p>
            </div>
          </div>
        </div>
        <div className="glass rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{users.filter(u => u.must_reset_password).length}</p>
              <p className="text-slate-400 text-sm">Pending Setup</p>
            </div>
          </div>
        </div>
        <div 
          className="glass rounded-xl p-5 cursor-pointer hover:bg-white/5 transition-colors"
          onClick={() => { setBroadcastType("inactive"); setShowBroadcastModal(true); }}
          data-testid="inactive-users-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{inactiveUsers.length}</p>
              <p className="text-slate-400 text-sm">Inactive (4+ days)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowInviteForm(false)}>
          <div className="glass rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="invite-form-modal">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-brand-primary" />
              Invite New User
            </h2>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <Label className="text-slate-400">Name</Label>
                <Input
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className="bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-600 focus:border-brand-primary"
                  data-testid="invite-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-400">Email</Label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  className="bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-600 focus:border-brand-primary"
                  data-testid="invite-email-input"
                />
              </div>
              <div className="p-3 rounded-lg bg-brand-primary/10 border border-brand-primary/30">
                <p className="text-sm text-brand-primary">
                  User will receive an invitation email with default password: <strong>ClarityOS2026</strong>
                </p>
              </div>
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowInviteForm(false)}
                  className="flex-1 border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={inviting}
                  className="flex-1 bg-brand-primary text-clarity-bg font-bold hover:bg-yellow-400"
                  data-testid="send-invite-btn"
                >
                  {inviting ? "Sending..." : "Send Invitation"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Broadcast Modal */}
      {showCustomBroadcast && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !sendingCustom && setShowCustomBroadcast(false)}>
          <div className="bg-[var(--surface2)] border border-[var(--border2)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[var(--lock)]/15 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[var(--lock)]" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-[var(--text)]">Custom Broadcast</h2>
                  <p className="text-xs text-[var(--muted)] mt-0.5">Compose and send to selected users</p>
                </div>
              </div>
              {!sendingCustom && (
                <button onClick={() => setShowCustomBroadcast(false)} className="text-[var(--muted)] hover:text-[var(--text)] transition-colors">
                  <span className="text-lg leading-none">x</span>
                </button>
              )}
            </div>

            <div className="p-6 space-y-5">

              {/* Recipient Mode */}
              <div>
                <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wider mb-2 block">Send To</label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { key: "all", label: "All Users", count: users.length },
                    { key: "active", label: "Active", count: users.filter(u => u.is_active !== false).length },
                    { key: "inactive", label: "Inactive", count: inactiveUsers.length },
                    { key: "selected", label: "Pick Users", count: selectedRecipients.length }
                  ].map(({ key, label, count }) => (
                    <button
                      key={key}
                      onClick={() => setCustomRecipientMode(key)}
                      className={`p-2.5 rounded-lg border text-sm transition-all ${
                        customRecipientMode === key
                          ? "border-[var(--lock)] bg-[var(--lock)]/10 text-[var(--lock)]"
                          : "border-[var(--border)] text-[var(--muted)] hover:border-[var(--border2)] hover:text-[var(--text)]"
                      }`}
                    >
                      <div className="font-medium">{label}</div>
                      <div className="text-xs opacity-70 mt-0.5">{count}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* User Picker (only shown in "selected" mode) */}
              {customRecipientMode === "selected" && (
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wider mb-2 block">
                    Select Recipients - {selectedRecipients.length} selected
                  </label>
                  <div className="border border-[var(--border)] rounded-lg divide-y divide-[var(--border)] max-h-40 overflow-y-auto">
                    {users.map(u => (
                      <label key={u.email} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/3 transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedRecipients.includes(u.email)}
                          onChange={() => toggleRecipient(u.email)}
                          className="accent-[var(--lock)]"
                        />
                        <span className="text-sm text-[var(--text)]">{u.name || u.email}</span>
                        <span className="text-xs text-[var(--dim)] ml-auto">{u.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wider mb-2 block">Subject Line</label>
                <input
                  type="text"
                  value={customSubject}
                  onChange={e => setCustomSubject(e.target.value)}
                  placeholder="e.g. Your ClarityOS weekly update"
                  className="w-full bg-[var(--surface3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--text)] placeholder-[var(--dim)] focus:outline-none focus:border-[var(--lock)]/50 transition-colors"
                />
              </div>

              {/* Body */}
              <div>
                <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wider mb-2 block">Message Body</label>
                <textarea
                  value={customBody}
                  onChange={e => setCustomBody(e.target.value)}
                  placeholder={"Dear [User Name],\n\nWrite your message here...\n\n- Debu Mishra"}
                  rows={7}
                  className="w-full bg-[var(--surface3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--text)] placeholder-[var(--dim)] focus:outline-none focus:border-[var(--lock)]/50 transition-colors resize-none font-mono"
                />
                <p className="text-xs text-[var(--dim)] mt-1">Use [User Name] to personalise - it will be replaced with each recipient's name.</p>
              </div>

              {/* Preview */}
              {(customSubject || customBody) && (
                <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg p-4">
                  <p className="text-xs text-[var(--dim)] uppercase tracking-wider mb-2">Preview</p>
                  {customSubject && <p className="text-sm font-medium text-[var(--text)] mb-2">Subject: {customSubject}</p>}
                  {customBody && (
                    <p className="text-sm text-[var(--muted)] whitespace-pre-wrap">
                      {customBody.replace("[User Name]", "Debu")}
                    </p>
                  )}
                </div>
              )}

              {/* Send result */}
              {customSentResult && (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${customSentResult.success ? "bg-green-500/10 border border-green-500/20 text-green-400" : "bg-red-500/10 border border-red-500/20 text-red-400"}`}>
                  {customSentResult.success
                    ? <><CheckCircle2 className="w-4 h-4" /> Sent to {customSentResult.count} recipients{customSentResult.failed > 0 ? ` (${customSentResult.failed} failed)` : ""}</>
                    : <><AlertCircle className="w-4 h-4" /> Send failed - check logs</>
                  }
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 px-6 pb-6">
              <div className="text-xs text-[var(--dim)]">
                {getRecipientCount()} recipient{getRecipientCount() !== 1 ? "s" : ""}
                {customRecipientMode === "selected" && selectedRecipients.length === 0 && (
                  <span className="text-amber-400 ml-1">- select at least one</span>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCustomBroadcast(false)}
                  disabled={sendingCustom}
                  className="border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendCustomBroadcast}
                  disabled={sendingCustom || !customSubject.trim() || !customBody.trim() || (customRecipientMode === "selected" && selectedRecipients.length === 0)}
                  className="bg-[var(--lock)] text-[var(--bg)] hover:opacity-90 font-semibold disabled:opacity-40"
                >
                  {sendingCustom ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />Send to {getRecipientCount()}</>
                  )}
                </Button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Broadcast Email Modal */}
      {showBroadcastModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBroadcastModal(false)}>
          <div className="glass rounded-2xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="broadcast-modal">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              {broadcastType === "transition" ? (
                <>
                  <Megaphone className="w-5 h-5 text-brand-tertiary" />
                  Broadcast: App Transition Announcement
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Send Inactive User Warnings
                </>
              )}
            </h2>
            
            {/* Email Preview */}
            <div className="bg-clarity-subtle/50 rounded-lg p-4 mb-4 border border-white/10">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Email Preview</p>
              {broadcastType === "transition" ? (
                <div className="text-sm text-slate-300 space-y-3">
                  <p className="font-medium text-white">Subject: ClarityOS is Evolving - Important Update</p>
                  <p>Dear [User Name],</p>
                  <p>We're excited to announce that ClarityOS is transitioning to a new and improved version!</p>
                  <p>Here's what you need to know:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-400">
                    <li>New premium features are coming soon</li>
                    <li>Your progress and journal entries will be preserved</li>
                    <li>As a valued beta user, you'll receive special benefits</li>
                  </ul>
                  <p>Stay tuned for more details. Thank you for being part of our journey!</p>
                  <p className="text-slate-500">- Debu Mishra</p>
                </div>
              ) : (
                <div className="text-sm text-slate-300 space-y-3">
                  <p className="font-medium text-white">Subject: Your ClarityOS Access - Action Required</p>
                  <p>Dear [User Name],</p>
                  <p>We noticed you haven't used ClarityOS in [X] days.</p>
                  <p className="text-amber-400 font-medium">Your access will be revoked after 4 consecutive days of inactivity.</p>
                  <p>To continue your Beta Access, please complete one session.</p>
                  <p>We'd love to see you back on your journey to mental clarity!</p>
                  <p className="text-slate-500">- Debu Mishra</p>
                </div>
              )}
            </div>
            
            {/* Recipients Info */}
            <div className={`p-3 rounded-lg mb-4 ${broadcastType === "transition" ? "bg-brand-tertiary/10 border border-brand-tertiary/30" : "bg-amber-500/10 border border-amber-500/30"}`}>
              <p className={`text-sm ${broadcastType === "transition" ? "text-brand-tertiary" : "text-amber-400"}`}>
                {broadcastType === "transition" ? (
                  <>Will be sent to <strong>{users.length}</strong> beta users</>
                ) : (
                  <>Will be sent to <strong>{inactiveUsers.length}</strong> inactive users (4+ days without activity)</>
                )}
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowBroadcastModal(false)}
                className="flex-1 border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handleBroadcastEmail(broadcastType)}
                disabled={sendingBroadcast || (broadcastType === "inactive" && inactiveUsers.length === 0)}
                className={`flex-1 font-bold ${broadcastType === "transition" ? "bg-brand-tertiary text-clarity-bg hover:bg-teal-400" : "bg-amber-500 text-clarity-bg hover:bg-amber-400"}`}
                data-testid="send-broadcast-btn"
              >
                {sendingBroadcast ? "Sending..." : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send to {broadcastType === "transition" ? "All Users" : `${inactiveUsers.length} Users`}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── ALLOCATE ACCESS MODAL ──────────────────────────────────────────── */}
      {showAllocateModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !allocating && setShowAllocateModal(false)}>
          <div className="bg-[#0f1114] border border-[rgba(255,255,255,0.09)] rounded-2xl w-full max-w-lg flex flex-col" style={{maxHeight:"90vh"}} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[rgba(255,255,255,0.07)]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{background:"rgba(76,175,122,0.12)"}}>
                  <CalendarPlus className="w-4 h-4" style={{color:"#4CAF7A"}}/>
                </div>
                <div>
                  <h2 className="text-[15px] font-semibold" style={{color:"#e8e2d6"}}>Allocate Access</h2>
                  <p className="text-[11px] mt-0.5" style={{color:"rgba(237,232,223,0.45)"}}>Extend premium access without payment</p>
                </div>
              </div>
              {!allocating && (
                <button onClick={() => setShowAllocateModal(false)} className="w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:bg-white/8" style={{color:"rgba(237,232,223,0.4)"}}>
                  <X className="w-4 h-4"/>
                </button>
              )}
            </div>

            {/* Duration picker — always visible */}
            <div className="px-6 pt-5 pb-4 border-b border-[rgba(255,255,255,0.07)]">
              <p className="text-[10px] font-medium uppercase tracking-[0.14em] mb-3" style={{color:"rgba(237,232,223,0.4)", fontFamily:"DM Mono, monospace"}}>
                Duration to add
              </p>
              <div className="grid grid-cols-5 gap-2">
                {[1, 3, 6, 12, "custom"].map(opt => (
                  <button
                    key={opt}
                    onClick={() => setAllocateMonths(opt)}
                    className="py-2.5 rounded-lg text-[12px] transition-all"
                    style={{
                      background: allocateMonths === opt ? "rgba(76,175,122,0.15)" : "rgba(255,255,255,0.03)",
                      border: `1px solid ${allocateMonths === opt ? "rgba(76,175,122,0.45)" : "rgba(255,255,255,0.07)"}`,
                      color: allocateMonths === opt ? "#4CAF7A" : "rgba(237,232,223,0.55)",
                      fontFamily: "DM Mono, monospace"
                    }}
                  >
                    {opt === "custom" ? "Custom" : `${opt}mo`}
                  </button>
                ))}
              </div>
              {allocateMonths === "custom" && (
                <div className="mt-3 flex items-center gap-3">
                  <input
                    type="number" min={1} max={36}
                    value={allocateCustomMonths}
                    onChange={e => setAllocateCustomMonths(e.target.value)}
                    placeholder="e.g. 2"
                    className="w-24 px-3 py-2 rounded-lg text-[13px] focus:outline-none"
                    style={{background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.12)", color:"#e8e2d6"}}
                    autoFocus
                  />
                  <span className="text-[12px]" style={{color:"rgba(237,232,223,0.45)"}}>months</span>
                </div>
              )}
            </div>

            {/* Tab bar */}
            <div className="flex border-b border-[rgba(255,255,255,0.07)]">
              {[
                { key: "users", label: `Users${allocateSelected.length > 0 ? ` (${allocateSelected.length})` : ""}` },
                { key: "message", label: allocateMessage.trim() ? "Message ✓" : "Message" }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setAllocateTab(tab.key)}
                  className="flex-1 py-3 text-[11px] font-medium uppercase tracking-[0.12em] transition-colors"
                  style={{
                    fontFamily: "DM Mono, monospace",
                    color: allocateTab === tab.key ? "#4CAF7A" : "rgba(237,232,223,0.38)",
                    borderBottom: `2px solid ${allocateTab === tab.key ? "#4CAF7A" : "transparent"}`,
                    marginBottom: -1,
                    background: "transparent"
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── USERS TAB ── */}
            {allocateTab === "users" && (
              <>
                <div className="px-6 pt-4 pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-medium uppercase tracking-[0.14em]" style={{color:"rgba(237,232,223,0.4)", fontFamily:"DM Mono, monospace"}}>
                      {allocateSelected.length > 0 ? `${allocateSelected.length} selected` : "Select users"}
                    </p>
                    <button
                      onClick={selectAllFiltered}
                      className="text-[10px] font-medium transition-colors"
                      style={{color:"rgba(76,175,122,0.75)", fontFamily:"DM Mono, monospace"}}
                    >
                      {(() => {
                        const filtered = users.filter(u =>
                          u.email.toLowerCase().includes(allocateSearch.toLowerCase()) ||
                          u.name.toLowerCase().includes(allocateSearch.toLowerCase())
                        );
                        return filtered.length > 0 && filtered.every(u => allocateSelected.includes(u.email))
                          ? "Deselect all" : "Select all";
                      })()}
                    </button>
                  </div>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{color:"rgba(237,232,223,0.3)"}}/>
                    <input
                      type="text"
                      value={allocateSearch}
                      onChange={e => setAllocateSearch(e.target.value)}
                      placeholder="Search by name or email..."
                      className="w-full pl-9 pr-8 py-2 rounded-lg text-[12px] focus:outline-none"
                      style={{background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)", color:"#e8e2d6"}}
                    />
                    {allocateSearch && (
                      <button onClick={() => setAllocateSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2" style={{color:"rgba(237,232,223,0.3)"}}>
                        <X className="w-3 h-3"/>
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0" style={{maxHeight:260}}>
                  {(() => {
                    const filtered = users.filter(u =>
                      u.email.toLowerCase().includes(allocateSearch.toLowerCase()) ||
                      u.name.toLowerCase().includes(allocateSearch.toLowerCase())
                    );
                    if (filtered.length === 0) return (
                      <div className="py-8 text-center text-[12px]" style={{color:"rgba(237,232,223,0.35)"}}>No users match "{allocateSearch}"</div>
                    );
                    return (
                      <div className="space-y-1">
                        {filtered.map(u => {
                          const selected = allocateSelected.includes(u.email);
                          return (
                            <button
                              key={u.email}
                              onClick={() => toggleAllocateUser(u.email)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all"
                              style={{
                                background: selected ? "rgba(76,175,122,0.08)" : "transparent",
                                border: `1px solid ${selected ? "rgba(76,175,122,0.25)" : "transparent"}`,
                              }}
                            >
                              <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all" style={{
                                background: selected ? "#4CAF7A" : "transparent",
                                border: `1.5px solid ${selected ? "#4CAF7A" : "rgba(255,255,255,0.2)"}`,
                              }}>
                                {selected && <Check className="w-2.5 h-2.5" style={{color:"#0a0c0f"}}/>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] truncate" style={{color:"#e8e2d6"}}>{u.name}</p>
                                <p className="text-[11px] truncate" style={{color:"rgba(237,232,223,0.4)"}}>{u.email}</p>
                              </div>
                              <div className="flex-shrink-0 text-right">
                                {u.beta_mode && u.beta_expiry ? (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{background:"rgba(201,168,76,0.12)", color:"#C9A84C", fontFamily:"DM Mono, monospace"}}>
                                    until {formatDate(u.beta_expiry)}
                                  </span>
                                ) : (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{background:"rgba(255,255,255,0.05)", color:"rgba(237,232,223,0.3)", fontFamily:"DM Mono, monospace"}}>
                                    no access
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </>
            )}

            {/* ── MESSAGE TAB ── */}
            {allocateTab === "message" && (
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" style={{maxHeight:340}}>

                {/* Optional label */}
                <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:"rgba(76,175,122,0.06)", border:"1px solid rgba(76,175,122,0.12)"}}>
                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{color:"rgba(76,175,122,0.7)"}}/>
                  <p className="text-[11px]" style={{color:"rgba(237,232,223,0.5)"}}>
                    Optional — leave blank to allocate silently without sending an email.
                  </p>
                </div>

                {/* Subject */}
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.14em] block mb-2" style={{color:"rgba(237,232,223,0.4)", fontFamily:"DM Mono, monospace"}}>
                    Subject line
                  </label>
                  <input
                    type="text"
                    value={allocateSubject}
                    onChange={e => setAllocateSubject(e.target.value)}
                    placeholder="e.g. Your ClarityOS access has been extended"
                    className="w-full px-3 py-2.5 rounded-lg text-[13px] focus:outline-none"
                    style={{background:"rgba(255,255,255,0.05)", border:`1px solid ${allocateSubject ? "rgba(76,175,122,0.35)" : "rgba(255,255,255,0.08)"}`, color:"#e8e2d6", transition:"border-color 0.2s"}}
                  />
                </div>

                {/* Message body */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[10px] font-medium uppercase tracking-[0.14em]" style={{color:"rgba(237,232,223,0.4)", fontFamily:"DM Mono, monospace"}}>
                      Personal message
                    </label>
                    <span className="text-[10px]" style={{color:"rgba(237,232,223,0.25)", fontFamily:"DM Mono, monospace"}}>
                      [First Name] auto-inserts name
                    </span>
                  </div>
                  <textarea
                    value={allocateMessage}
                    onChange={e => setAllocateMessage(e.target.value)}
                    rows={6}
                    placeholder={"[First Name],\n\nI've extended your ClarityOS access by " + (getEffectiveMonths() > 0 ? getEffectiveMonths() + " month" + (getEffectiveMonths() !== 1 ? "s" : "") : "...") + ".\n\nLooking forward to hearing how the protocol is working for you.\n\n— Debu"}
                    className="w-full px-3 py-2.5 rounded-lg text-[13px] focus:outline-none resize-none"
                    style={{
                      background:"rgba(255,255,255,0.05)",
                      border:`1px solid ${allocateMessage ? "rgba(76,175,122,0.35)" : "rgba(255,255,255,0.08)"}`,
                      color:"#e8e2d6",
                      fontFamily:"DM Mono, monospace",
                      lineHeight:1.7,
                      transition:"border-color 0.2s"
                    }}
                  />
                </div>

                {/* Live preview */}
                {(allocateSubject || allocateMessage) && (
                  <div className="rounded-lg overflow-hidden" style={{border:"1px solid rgba(255,255,255,0.07)"}}>
                    <div className="px-3 py-2 flex items-center gap-2" style={{background:"rgba(255,255,255,0.04)", borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                      <div className="w-2 h-2 rounded-full" style={{background:"rgba(76,175,122,0.5)"}}/>
                      <span className="text-[10px] uppercase tracking-[0.12em]" style={{color:"rgba(237,232,223,0.3)", fontFamily:"DM Mono, monospace"}}>Email preview</span>
                    </div>
                    <div className="px-4 py-4" style={{background:"#0D0F14"}}>
                      {allocateSubject && (
                        <p className="text-[12px] font-medium mb-3" style={{color:"rgba(237,232,223,0.5)", fontFamily:"DM Mono, monospace"}}>
                          Subject: {allocateSubject}
                        </p>
                      )}
                      {allocateMessage && (
                        <p className="text-[13px] whitespace-pre-wrap" style={{color:"rgba(237,232,223,0.75)", lineHeight:1.75}}>
                          {allocateMessage.replace(/\[First Name\]/g, allocateSelected.length === 1
                            ? (users.find(u => u.email === allocateSelected[0])?.name?.split(" ")[0] || "there")
                            : "[First Name]"
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* Footer */}
            <div className="px-6 py-4 border-t border-[rgba(255,255,255,0.07)] flex items-center justify-between gap-3">
              <div className="text-[11px]" style={{color:"rgba(237,232,223,0.4)", fontFamily:"DM Mono, monospace"}}>
                {allocateSelected.length > 0 && getEffectiveMonths() > 0 ? (
                  <span style={{color:"rgba(76,175,122,0.8)"}}>
                    {allocateSelected.length} user{allocateSelected.length !== 1 ? "s" : ""} · +{getEffectiveMonths()}mo
                    {allocateMessage.trim() ? " · with message" : " · silent"}
                  </span>
                ) : allocateSelected.length === 0 ? (
                  <span>Select users first</span>
                ) : (
                  <span style={{color:"rgba(201,168,76,0.7)"}}>Set a duration</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAllocateModal(false)}
                  disabled={allocating}
                  className="border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAllocateAccess}
                  disabled={allocating || allocateSelected.length === 0 || getEffectiveMonths() < 1}
                  style={{background:"#4CAF7A", color:"#0a0c0f"}}
                  className="font-semibold hover:opacity-90 disabled:opacity-40"
                  data-testid="confirm-allocate-btn"
                >
                  {allocating ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin"/>Allocating...</>
                  ) : (
                    <><CalendarPlus className="w-4 h-4 mr-2"/>
                    {allocateMessage.trim() ? "Allocate + Send" : "Allocate Access"}</>
                  )}
                </Button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Beta Access Modal */}
      {showBetaModal && betaUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBetaModal(false)}>
          <div className="glass rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="beta-access-modal">
            <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <Crown className="w-5 h-5 text-[#C8860A]" />
              Beta Access
            </h2>
            <p className="text-slate-400 text-sm mb-6">
              {betaUser.name} ({betaUser.email})
            </p>
            
            {betaUser.beta_mode ? (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-[#C8860A]/10 border border-[#C8860A]/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-4 h-4 text-[#C8860A]" />
                    <span className="text-[#C8860A] font-medium">Founding Member</span>
                  </div>
                  <p className="text-slate-400 text-sm">
                    This user has beta access until {formatDate(betaUser.beta_expiry)}
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowBetaModal(false)}
                    className="flex-1 border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    Close
                  </Button>
                  <Button 
                    onClick={() => handleBetaAccessToggle(false)}
                    disabled={updatingBeta}
                    className="flex-1 bg-red-500/20 text-red-400 hover:bg-red-500/30"
                  >
                    {updatingBeta ? "Revoking..." : "Revoke Access"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400">Access Duration (days)</Label>
                  <Input
                    type="number"
                    value={betaDays}
                    onChange={(e) => setBetaDays(parseInt(e.target.value) || 90)}
                    min={7}
                    max={365}
                    className="bg-clarity-subtle/50 border-white/10 text-white focus:border-[#C8860A]"
                    data-testid="beta-days-input"
                  />
                  <p className="text-slate-500 text-xs mt-1">Default: 90 days for founding members</p>
                </div>
                <div className="p-3 rounded-lg bg-[#C8860A]/10 border border-[#C8860A]/30">
                  <p className="text-sm text-[#C8860A]">
                    Granting beta access will send an email notification and show a welcome banner on their next login.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowBetaModal(false)}
                    className="flex-1 border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => handleBetaAccessToggle(true)}
                    disabled={updatingBeta}
                    className="flex-1 bg-[#C8860A] text-[#0D0F14] font-bold hover:bg-[#D4963A]"
                    data-testid="grant-beta-btn"
                  >
                    {updatingBeta ? "Granting..." : (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        Grant Beta Access
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab("users")}
          className={`px-4 py-2 text-[11px] uppercase tracking-wider transition-colors ${
            activeTab === "users"
              ? "bg-[#C8860A] text-[#0D0F14]"
              : "bg-[#1C2130] text-[#9BA8BE] hover:text-white"
          }`}
          style={{ fontFamily: 'DM Mono, monospace', borderRadius: '2px' }}
        >
          <Users className="w-4 h-4 inline-block mr-2" />
          Users
        </button>
        <button
          onClick={() => setActiveTab("book")}
          className={`px-4 py-2 text-[11px] uppercase tracking-wider transition-colors relative ${
            activeTab === "book"
              ? "bg-[#C8860A] text-[#0D0F14]"
              : "bg-[#1C2130] text-[#9BA8BE] hover:text-white"
          }`}
          style={{ fontFamily: 'DM Mono, monospace', borderRadius: '2px' }}
        >
          <BookOpen className="w-4 h-4 inline-block mr-2" />
          Book Verifications
          {bookClaims.filter(c => c.status === "pending_verification").length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#D4723A] text-white text-[10px] flex items-center justify-center">
              {bookClaims.filter(c => c.status === "pending_verification").length}
            </span>
          )}
        </button>
      </div>

      {activeTab === "users" && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users..."
              className="pl-10 bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-600 focus:border-brand-primary"
              data-testid="search-users-input"
            />
          </div>

      {/* Users Table */}
      <div className="glass rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left p-4 text-slate-400 font-medium">User</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Completions</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Joined</th>
                  <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.email} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${u.email}`}>
                    <td className="p-4">
                      <div>
                        <p className="text-white font-medium">{u.name}</p>
                        <p className="text-slate-500 text-sm">{u.email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        {u.beta_mode && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#C8860A]/10 text-[#C8860A] text-xs">
                            <Crown className="w-3 h-3" />
                            Beta
                          </span>
                        )}
                        {u.must_reset_password ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-400 text-xs">
                            <Clock className="w-3 h-3" />
                            Pending
                          </span>
                        ) : u.is_active !== false ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/10 text-green-400 text-xs">
                            <CheckCircle2 className="w-3 h-3" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 text-red-400 text-xs">
                            <AlertCircle className="w-3 h-3" />
                            Disabled
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-white">{u.total_completions || 0}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400 text-sm">{formatDate(u.created_at)}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        {/* Beta Access Toggle */}
                        <button
                          onClick={() => openBetaModal(u)}
                          className={`p-2 rounded-lg transition-colors ${
                            u.beta_mode 
                              ? "text-[#C8860A] hover:bg-[#C8860A]/10" 
                              : "text-slate-400 hover:text-[#C8860A] hover:bg-[#C8860A]/10"
                          }`}
                          title={u.beta_mode ? "Manage beta access" : "Grant beta access"}
                          data-testid={`beta-access-${u.email}`}
                        >
                          <Crown className={`w-5 h-5 ${u.beta_mode ? "fill-current" : ""}`} />
                        </button>
                        {/* Resend Invitation - only for pending users */}
                        {u.must_reset_password && (
                          <button
                            onClick={() => handleResendInvitation(u.email, u.name)}
                            className="p-2 rounded-lg text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 transition-colors"
                            title="Resend invitation email"
                            data-testid={`resend-invite-${u.email}`}
                          >
                            <Send className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleUser(u.email, u.is_active !== false)}
                          className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                          title={u.is_active !== false ? "Deactivate user" : "Activate user"}
                          data-testid={`toggle-user-${u.email}`}
                        >
                          {u.is_active !== false ? (
                            <ToggleRight className="w-5 h-5 text-green-400" />
                          ) : (
                            <ToggleLeft className="w-5 h-5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleRemoveUser(u.email)}
                          className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Remove user"
                          data-testid={`remove-user-${u.email}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Email Status Notice */}
      <div className="glass rounded-xl p-4 border border-green-500/20">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-medium">Email Service Status</p>
            <p className="text-slate-400 text-sm mt-1">
              Resend is configured. Invitation emails will be sent automatically to users.
            </p>
          </div>
        </div>
      </div>
        </>
      )}

      {/* Book Verifications Tab */}
      {activeTab === "book" && (
        <div className="space-y-6">
          {loadingClaims ? (
            <div className="p-12 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-[#C8860A] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : bookClaims.length === 0 ? (
            <div className="bg-[#1C2130] border border-[rgba(255,255,255,0.07)] rounded-sm p-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No book verification claims yet</p>
            </div>
          ) : (
            <div className="bg-[#1C2130] border border-[rgba(255,255,255,0.07)] rounded-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(255,255,255,0.07)]">
                    <th className="text-left p-4 text-[#9BA8BE] text-[11px] uppercase tracking-wider" style={{ fontFamily: 'DM Mono, monospace' }}>Email</th>
                    <th className="text-left p-4 text-[#9BA8BE] text-[11px] uppercase tracking-wider" style={{ fontFamily: 'DM Mono, monospace' }}>Order Number</th>
                    <th className="text-left p-4 text-[#9BA8BE] text-[11px] uppercase tracking-wider" style={{ fontFamily: 'DM Mono, monospace' }}>Submitted</th>
                    <th className="text-left p-4 text-[#9BA8BE] text-[11px] uppercase tracking-wider" style={{ fontFamily: 'DM Mono, monospace' }}>Status</th>
                    <th className="text-right p-4 text-[#9BA8BE] text-[11px] uppercase tracking-wider" style={{ fontFamily: 'DM Mono, monospace' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookClaims.map((claim) => (
                    <tr key={claim.claim_id} className="border-b border-[rgba(255,255,255,0.07)] hover:bg-[#232A3B]">
                      <td className="p-4 text-white text-[13px]">{claim.email}</td>
                      <td className="p-4 text-[#9BA8BE] text-[13px] font-mono">{claim.order_number}</td>
                      <td className="p-4 text-[#9BA8BE] text-[13px]">
                        {new Date(claim.submitted_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="p-4">
                        {claim.status === "pending_verification" && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-400 text-xs">
                            <Clock className="w-3 h-3" />
                            Pending
                          </span>
                        )}
                        {claim.status === "approved" && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/10 text-green-400 text-xs">
                            <CheckCircle2 className="w-3 h-3" />
                            Approved
                          </span>
                        )}
                        {claim.status === "rejected" && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 text-red-400 text-xs">
                            <AlertCircle className="w-3 h-3" />
                            Rejected
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        {claim.status === "pending_verification" && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              onClick={() => handleBookClaimAction(claim.claim_id, "approve")}
                              className="bg-[#4CAF82] text-white hover:bg-[#3d9a6a] text-[11px] h-8 px-3"
                            >
                              Approve
                            </Button>
                            <Button
                              onClick={() => handleBookClaimAction(claim.claim_id, "reject")}
                              variant="ghost"
                              className="text-red-400 hover:bg-red-500/10 text-[11px] h-8 px-3"
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
